import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  DataContextBuilder,
  ElderBasicPaneLayoutComponent,
  ElderNavLinkComponent,
  ElderNavListComponent,
  ElderPaneComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderTableComponent,
  ElderToolbarColumnDirective,
  Filter,
  IDataContext,
  Pageable,
  Sort,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { Observable } from 'rxjs'
import { filter, map, share } from 'rxjs/operators'
import { FacilityContextMenuComponent } from '../../../context/facility-context/facility-context-menu/facility-context-menu.component'
import { FacilityContextDirective } from '../../../context/facility-context/facility-context.directive'
import { IntervalContextMenuComponent } from '../../../context/interval-context/interval-context-menu/interval-context-menu.component'
import { Facility } from '../../../facilities/facility'
import { FilterBuilder } from '../../../orders/filter-builder'
import { ProcessPlan } from '../../../process/process-plans/process-plan'
import { ProcessPlanService } from '../../../process/process-plans/process-plan.service'
import { ProductionLineRoute } from '../production-line-route'
import { ProductionLineRouteService } from '../production-line-route.service'
import { ProductionRouteComponent } from '../production-route/production-route.component'

@Component({
  selector: 'app-production-line-route-browser',
  templateUrl: './production-line-route-browser.component.html',
  styleUrls: ['./production-line-route-browser.component.scss'],
  imports: [
    ElderToolbarColumnDirective,
    FacilityContextMenuComponent,
    IntervalContextMenuComponent,
    FacilityContextDirective,
    ElderNavListComponent,
    ElderNavLinkComponent,
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    ProductionRouteComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
  ],
})
export class ProductionLineRouteBrowserComponent {
  private productionLinePlanService = inject(ProductionLineRouteService)
  private processPlanService = inject(ProcessPlanService)

  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  readonly data: IDataContext<ProductionLineRoute>
  readonly availablePlans$: Observable<ProcessPlan[]>
  readonly selectedPlan$: Observable<ProcessPlan>

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<ProductionLineRoute>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .autoStartOnFiltersAll(
        [FilterBuilder.FACILITY_KEY, 'processPlan'],
        [new Sort('createdAt', 'desc')]
      )
      .build(this.productionLinePlanService)

    this.availablePlans$ = this.processPlanService.findAllPaged(new Pageable(0, 100)).pipe(
      map((page) => page.content),
      share()
    )
    this.selectedPlan$ = this.availablePlans$.pipe(
      filter((plans) => plans.length > 0),
      map((plans) => plans[0])
    )
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  comparePlans(a: ProcessPlan, b: ProcessPlan): boolean {
    return !!a && !!b && a.id === b.id
  }

  updateFacilityFilter(facility: Facility) {
    if (facility) {
      this.log.debug(`Updating filter with facility ${facility.id}`)
      this.filters = [FilterBuilder.facility(facility.id)]
    }
  }

  updatePlanFilter(processPlan: ProcessPlan) {
    if (processPlan) {
      this.log.debug(`Updating filter with process plan ${processPlan.id}`)
      this.filters = [new Filter('processPlan', processPlan.id)]
    }
  }

  set filters(filters: Filter[]) {
    this.data.filter.updateFilters(filters)
  }
}
